import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils.ts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card.tsx";
import { Button } from "@/ui/components/ui/button.tsx";
import { Loader2 } from "lucide-react";
import TextInputController from "@/shared/components/ui/form/input-with-controller.tsx";

const ForgotPasswordSchema = z.object({
  email: z
    .string({
      required_error: "validation.required",
    })
    .min(1, { message: "validation.required" })
    .email({ message: "validation.invalidEmail" }),
});

export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

interface ForgotPasswordFormProps {
  onSubmit: (data: ForgotPasswordInput) => void;
  isLoading: boolean;
}

const ForgotPasswordForm = ({ onSubmit, isLoading }: ForgotPasswordFormProps) => {
  const { t } = useTranslation("auth");
  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = async (data: ForgotPasswordInput) => {
    onSubmit(data);
  };

  return (
    <div className={cn("flex flex-col gap-6 max-w-96 mx-auto")}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t("forgotPassword.title")}</CardTitle>
          <CardDescription>
            {t("forgotPassword.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="grid gap-6">
                <div className="grid gap-4">
                  <TextInputController placeholder={t("forgotPassword.form.emailPlaceholder")} title={t("forgotPassword.form.emailLabel")} name="email" />
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="animate-spin mr-2" />}
                    {t("forgotPassword.form.submit")}
                  </Button>
                </div>
                <div className="text-center text-sm">
                  {t("forgotPassword.signInPrompt")}{" "}
                  <a href="#" className="underline underline-offset-4">
                    {t("forgotPassword.signInLink")}
                  </a>
                </div>
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordForm;
